import { supabase } from "./supabase";

/* First-party analytics (Launch readiness — Workstream B1). Events are buffered
   and flushed in BATCHES to the `ingest` Edge Function (the only writer to
   public.events), to keep us well under the Edge Function invocation cap — one
   request per ~10s / page-hide instead of one per event. Strictly
   fire-and-forget: never awaited, never throws, failures swallowed.

   We use fetch(keepalive) rather than supabase.functions.invoke so the flush
   survives page unload and we control batching. The ingest function is a
   public ingest endpoint (verify_jwt = false); we still pass the user's access
   token when signed in so the server can stamp user_id. A localStorage session
   id ties anonymous visits to the user once they sign up. */

const URL = import.meta.env.VITE_SUPABASE_URL;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const ENDPOINT = URL ? `${URL}/functions/v1/ingest` : null;

const FLUSH_MS = 10000;
const MAX_BATCH = 50;

let buffer = [];
let timer = null;
let userToken = null; // user access token when signed in; null when anonymous

// Keep the bearer token current: read once, then track auth changes.
supabase.auth.getSession().then(({ data }) => {
  userToken = data?.session?.access_token ?? null;
});
supabase.auth.onAuthStateChange((_e, session) => {
  userToken = session?.access_token ?? null;
});

const SID_KEY = "bj_sid";
function sessionId() {
  try {
    let s = localStorage.getItem(SID_KEY);
    if (!s) {
      s = crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(SID_KEY, s);
    }
    return s;
  } catch {
    return null;
  }
}

function flush() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!buffer.length || !ENDPOINT) return;
  const events = buffer;
  buffer = [];
  try {
    const headers = { "Content-Type": "application/json", apikey: KEY };
    if (userToken) headers.Authorization = `Bearer ${userToken}`;
    fetch(ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ events }), // JSON.stringify drops the _delivered fns
      keepalive: true, // survive page unload
    })
      .then((resp) => {
        if (!resp.ok) return;
        for (const ev of events) ev._delivered?.();
      })
      .catch(() => {});
  } catch {
    /* never let analytics throw */
  }
}

function schedule() {
  if (!timer) timer = setTimeout(flush, FLUSH_MS);
}

export function track(name, props = {}, onDelivered) {
  try {
    buffer.push({ name, props, session_id: sessionId(), _delivered: onDelivered });
    if (buffer.length >= MAX_BATCH) flush();
    else schedule();
  } catch {
    /* never let analytics throw */
  }
}

/* Fire at most once per browser — for "first_*" funnel events.
   The once-flag is set AFTER the batch actually lands (2xx from ingest):
   marking before delivery meant a funnel event fired offline was lost
   forever. The trade is at-least-once — a keepalive flush killed mid-unload
   can re-fire next session — which is the right side to err on for
   low-trust analytics. `onceInFlight` stops same-session repeats while the
   flag is still pending. */
const onceInFlight = new Set();
export function trackOnce(name, props = {}) {
  const k = `bj_evt_${name}`;
  try {
    if (localStorage.getItem(k)) return;
  } catch {
    /* storage unavailable — just fire */
  }
  if (onceInFlight.has(k)) return;
  onceInFlight.add(k);
  track(name, props, () => {
    try {
      localStorage.setItem(k, "1");
    } catch {
      /* best-effort */
    }
  });
}

// Flush the buffer when the tab is hidden or unloading (covers tab close,
// navigation away, and mobile backgrounding — more reliable than `unload`).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
}
